import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, RefreshCw, Radio } from "lucide-react";
import { Reveal, SectionTag, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const News = () => {
  const [articles, setArticles] = useState([]);
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/news`);
      setArticles(res.data.articles || []);
      setLive(res.data.live);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section
      id="news"
      data-testid="industry-news-section-container"
      className="bg-[#F8FAFC] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionTag index="08" label="Industry Pulse" testId="news-section-tag" />
        <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <SectionHeading>Promotional industry news</SectionHeading>
          </div>
          <Reveal delay={0.15}>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                <Radio className={`h-3.5 w-3.5 ${live ? "text-emerald-400" : "text-slate-500"}`} />
                {live ? "Live industry feeds" : "Curated updates"}
              </span>
              <button
                data-testid="industry-news-refresh-button"
                onClick={load}
                className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-[#6DC8F0]/60 hover:text-[#2E3192]"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading &&
            articles.length === 0 &&
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-52 animate-pulse rounded-2xl border border-slate-200 bg-white"
              />
            ))}

          {articles.map((a, i) => (
            <Reveal key={`${a.link}-${i}`} delay={Math.min(i, 5) * 0.08}>
              <a
                href={a.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={i === 0 ? "industry-news-card-item" : undefined}
                className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#6DC8F0]/40"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#2E3192]/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#2E3192]">
                    {a.source}
                  </span>
                  <span className="font-mono2 text-[10px] text-slate-500">{formatDate(a.published)}</span>
                </div>
                <h3 className="mt-4 flex-none font-display text-base font-bold leading-snug text-slate-900 transition-colors group-hover:text-[#2E3192]">
                  {a.title}
                </h3>
                <p className="mt-3 flex-1 text-xs leading-relaxed text-slate-500 line-clamp-3">
                  {a.summary}
                </p>
                <span className="mt-5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 transition-colors group-hover:text-[#2E3192]">
                  Read article
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default News;
